'use client';

import { useState, useCallback, useEffect } from 'react';
import { CloudArrowUpIcon } from '@heroicons/react/20/solid';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';

import { AppButton } from '../../../../forms';
import { MicrosoftOfficeExcelLogo } from '../../../../icons';

import { FileItem } from './FileItem';

interface IFileUploadDragAndDropProps {
  onFileChange: (files: File[]) => void;
}

/**
 * -----------------------------------------------------------------------------
 * It renders the file upload drag and drop component
 * TODO: Updates to this component are still a WIP
 */
export function FileUploadDragAndDrop({
  onFileChange,
}: Readonly<IFileUploadDragAndDropProps>) {
  const [files, setFiles] = useState<File[]>([]);

  const handleOnDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop: handleOnDrop,
  });

  const handleOnRemove = (fileToRemove: File) => {
    const newFiles = files.filter((file) => file.name !== fileToRemove.name);
    setFiles(newFiles);
  };

  // TODO: This is a temporary solution to trigger the onFileChange callback
  // TODO: Find an impertative solution for this.
  useEffect(() => {
    onFileChange(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  if (files.length === 0) {
    return (
      <div
        className={clsx(
          'border-spacing-2 rounded-3xl border-2 border-dotted px-2 py-6 text-center',
          isDragActive
            ? 'border-green-500 bg-green-50'
            : 'border-gray-200 bg-gray-50'
        )}
      >
        <div {...getRootProps({ className: 'dropzone' })}>
          <input {...getInputProps()} />

          <MicrosoftOfficeExcelLogo className="m-auto w-10" />

          <div className="text-lg font-bold text-green-900">
            Drag and drop the file here
          </div>

          <div className="pt-2 text-xs text-gray-600">
            <span>Or click and browse</span>
            <b>(max 5MB)</b>
          </div>
        </div>
      </div>
    );
  }

  const handleOnFileUpload = () => {
    // TODO: Add missing file upload logic
  };

  return (
    <div className="flex flex-col gap-2 text-sm">
      {files.map((file) => (
        <div
          key={file.name}
          className="
            flex flex-col items-center gap-10 rounded-2xl border
            border-green-800/20 px-5 py-3
          "
        >
          <div className="w-full rounded-md bg-gray-100 p-2">
            <div className="flex items-center gap-4">
              <div>
                <MicrosoftOfficeExcelLogo className="w-5 flex-shrink-0" />

                <div className="text-sm font-medium text-gray-900">
                  {file.name}
                </div>
              </div>

              <button
                className="cursor-pointer text-2xl text-gray-500"
                onClick={handleOnFileUpload}
                type="button"
              >
                &times;
              </button>
            </div>
            <p className="max-w-xs pt-1.5 text-xs text-gray-900">
              <span className="me-1">
                Thi attachment is ready for upload. Please tap click upload to
                continue
              </span>
              <b>(max 5 MB)</b>
            </p>
          </div>

          <div className="bg-red-500">
            <AppButton
              iconStart={<CloudArrowUpIcon />}
              isEnabled
              isLoading
              onClick={handleOnFileUpload}
              title="Upload File"
            />

            <FileItem
              key={file.name}
              file={file}
              onRemoveFile={handleOnRemove}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
